import { useState, useEffect } from 'react';
import { SentraWordmark } from '@/components/brand/SentraMark';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ROUTES } from '@/constants/routes';
import { AnimatedBackground } from '@/components/common/AnimatedBackground';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, loading, login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state)?.from?.pathname || ROUTES.DASHBOARD;

  useEffect(() => {
    if (token && !loading) {
      navigate(from, { replace: true });
    }
  }, [token, loading, navigate, from]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      await login(email, password);
    } catch {
      setError('Invalid email or password');
      setIsLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background */}
      <AnimatedBackground />
      
      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-md">
        {/* Login Card with integrated logo */}
        <Card className="w-full bg-white/95 backdrop-blur-sm shadow-2xl border-0 rounded-3xl overflow-hidden">
          {/* Logo section integrated into card header */}
          <CardHeader className="space-y-6 pt-8 pb-6 text-center">
            <div className="flex justify-center">
              <SentraWordmark size={44} />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-3xl font-bold text-gray-800">Welcome back</CardTitle>
              <CardDescription className="text-gray-600 text-base">
                Sign in to your account to continue
              </CardDescription>
            </div>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-5 px-8">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm border border-red-200 font-medium">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-semibold text-sm">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="name@example.com"
                  className="bg-white border-gray-300 focus:border-sidebar-accent focus:ring-sidebar-accent rounded-xl h-12 text-gray-700 placeholder:text-gray-400"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-semibold text-sm">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  className="bg-white border-gray-300 focus:border-sidebar-accent focus:ring-sidebar-accent rounded-xl h-12 text-gray-700"
                  required
                />
              </div>

              <div className="flex justify-end pt-2">
                <a 
                  href={ROUTES.FORGOT_PASSWORD} 
                  className="text-sm font-medium transition-colors duration-200 hover:opacity-80 text-sidebar-accent"
                >
                  Forgot password?
                </a>
              </div>
            </CardContent>
            
            <CardFooter className="px-8 pb-8 pt-4">
              <Button
                type="submit"
                className="w-full h-12 text-sidebar-foreground font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-base hover:opacity-90 bg-sidebar hover:bg-sidebar/90"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}